"use client";

import { useEffect, useRef, useState } from "react";

import moment from "moment";
import { useTranslations } from "next-intl";

import { TimelineTooltip } from "./TimelineTooltip";

type TimeRange = {
    end: moment.Moment;
    start: moment.Moment;
    type: "sleep" | "wake";
};

type TimelineGaugeProps = {
    bedTime: moment.Moment;
    naps: Array<{ end: moment.Moment; start: moment.Moment }>;
    wakeTime: moment.Moment;
};

const MINUTES_IN_DAY = 24 * 60;

export const TimelineGauge = ({ bedTime, naps, wakeTime }: TimelineGaugeProps) => {
    const t = useTranslations("TimelineGauge");
    const [activeRange, setActiveRange] = useState<null | number>(null);
    const circleRef = useRef<HTMLDivElement>(null);

    // Function to format duration for display
    function formatDuration(duration: moment.Duration): string;
    function formatDuration(start: moment.Moment, end: moment.Moment): string;
    function formatDuration(first: moment.Duration | moment.Moment, second?: moment.Moment): string {
        let duration: moment.Duration;

        if (moment.isDuration(first)) {
            // If first argument is already a duration
            duration = first;
        } else if (moment.isMoment(first) && second && moment.isMoment(second)) {
            // If two moments are provided (start and end)
            try {
                duration = moment.duration(second.diff(first));
            } catch (error) {
                console.error("Error calculating duration:", error);
                return "0" + t("timeUnits.minutesShort");
            }
        } else {
            // Fallback
            return "0" + t("timeUnits.minutesShort");
        }

        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        if (hours === 0) {
            return `${minutes}${t("timeUnits.minutesShort")}`;
        }
        return minutes === 0
            ? `${hours}${t("timeUnits.hoursShort")}`
            : `${hours}${t("timeUnits.hoursShort")} ${minutes}${t("timeUnits.minutesShort")}`;
    }

    // Convert times to minutes since wake time
    const getMinutesSinceWakeTime = (time: moment.Moment) => {
        const minutesSinceMidnight = time.hours() * 60 + time.minutes();
        const wakeMinutes = wakeTime.hours() * 60 + wakeTime.minutes();
        let minutesSinceWake = minutesSinceMidnight - wakeMinutes;

        // If the time is before wake time, add 24 hours
        if (minutesSinceWake < 0) {
            minutesSinceWake += MINUTES_IN_DAY;
        }

        return minutesSinceWake;
    };

    // Create array of sleep and wake ranges
    const buildTimeRanges = (): TimeRange[] => {
        const ranges: TimeRange[] = [];
        let currentTime = moment(wakeTime);

        // Add naps and wake windows
        naps.forEach((nap) => {
            ranges.push({
                end: nap.start,
                start: currentTime,
                type: "wake",
            });
            ranges.push({
                end: nap.end,
                start: nap.start,
                type: "sleep",
            });
            currentTime = moment(nap.end);
        });

        // Add final wake window until bedtime
        ranges.push({
            end: bedTime,
            start: currentTime,
            type: "wake",
        });

        // Add night sleep until next wake time
        const nextWakeTime = moment(wakeTime).add(1, "day");
        ranges.push({
            end: nextWakeTime,
            start: bedTime,
            type: "sleep",
        });

        return ranges;
    };

    const timeRanges = buildTimeRanges();

    // Get all unique times for markers (start and end times)
    const getTimeMarkers = () => {
        const times = new Set<number>();
        timeRanges.forEach((range) => {
            times.add(getMinutesSinceWakeTime(range.start));
            // For the last range (night sleep), use MINUTES_IN_DAY instead of end time
            if (range === timeRanges[timeRanges.length - 1]) {
                times.add(MINUTES_IN_DAY);
            } else {
                times.add(getMinutesSinceWakeTime(range.end));
            }
        });
        return Array.from(times).sort((a, b) => a - b);
    };

    const timeMarkers = getTimeMarkers();

    // Calculate the arc path for a segment of the ring
    const calculateArcPath = (
        startAngle: number,
        endAngle: number,
        outerRadius: number,
        innerRadius: number,
        centerX: number,
        centerY: number,
    ) => {
        // Convert angles from degrees to radians
        const startRad = (startAngle - 90) * (Math.PI / 180); // -90 to start at the top
        const endRad = (endAngle - 90) * (Math.PI / 180);

        // Calculate outer start and end points
        const outerX1 = centerX + outerRadius * Math.cos(startRad);
        const outerY1 = centerY + outerRadius * Math.sin(startRad);
        const outerX2 = centerX + outerRadius * Math.cos(endRad);
        const outerY2 = centerY + outerRadius * Math.sin(endRad);

        // Calculate inner start and end points
        const innerX1 = centerX + innerRadius * Math.cos(endRad);
        const innerY1 = centerY + innerRadius * Math.sin(endRad);
        const innerX2 = centerX + innerRadius * Math.cos(startRad);
        const innerY2 = centerY + innerRadius * Math.sin(startRad);

        // Determine if the arc should be drawn the long way around
        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        // Create the arc path with inner and outer arcs to form a ring segment
        return `
          M ${outerX1} ${outerY1} 
          A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerX2} ${outerY2}
          L ${innerX1} ${innerY1}
          A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerX2} ${innerY2}
          Z
        `;
    };

    // Calculate position for time labels around the circle
    const calculateLabelPosition = (angle: number, radius: number, centerX: number, centerY: number) => {
        const radians = (angle - 90) * (Math.PI / 180); // -90 to start at the top
        return {
            x: centerX + (radius + 20) * Math.cos(radians),
            y: centerY + (radius + 20) * Math.sin(radians),
        };
    };

    return (
        <div className="mt-6 flex justify-center">
            <div className="relative" ref={circleRef} style={{ height: "390px", width: "390px" }}>
                {/* Center content */}
                {/* Center content with drop shadow and glass effect */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center">
                    <div
                        className="w-[156px] h-[156px] rounded-full bg-white shadow-lg flex flex-col items-center justify-center"
                        style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1), inset 0 2px 6px rgba(255,255,255,0.9)" }}
                    >
                        {activeRange !== null ? (
                            <>
                                <div className="text-sm text-gray-600 mb-1">
                                    {timeRanges[activeRange].type === "sleep" ? t("sleep") : t("awake")} for
                                </div>
                                <div
                                    className="text-3xl font-bold"
                                    style={{ color: timeRanges[activeRange].type === "sleep" ? "#2667A7" : "#D4A76A" }}
                                >
                                    {formatDuration(timeRanges[activeRange].start, timeRanges[activeRange].end)}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                    {timeRanges[activeRange].start.format("HH:mm")} -{" "}
                                    {timeRanges[activeRange].end.format("HH:mm")}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-md text-black mb-1">{t("totalSleep")}</div>
                                <div className="text-3xl font-bold text-primary-100">
                                    {formatDuration(
                                        timeRanges.reduce((total, range) => {
                                            if (range.type === "sleep") {
                                                try {
                                                    // Safely calculate the duration and add it to the total
                                                    const rangeDuration = moment.duration(range.end.diff(range.start));
                                                    return total.add(rangeDuration);
                                                } catch (error) {
                                                    console.error("Error calculating sleep duration:", error, range);
                                                    return total;
                                                }
                                            }
                                            return total;
                                        }, moment.duration(0)),
                                    )}
                                </div>
                                <div className="text-md text-black mt-1">
                                    {Math.round(
                                        (timeRanges.reduce((total, range) => {
                                            if (range.type === "sleep") {
                                                return total + moment.duration(range.end.diff(range.start)).asHours();
                                            }
                                            return total;
                                        }, 0) /
                                            24) *
                                            100,
                                    )}
                                    % of day
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* SVG for the circular timeline */}
                {/* Add a subtle gradient background */}
                <div
                    className="absolute inset-0 rounded-full"
                    style={{ background: "radial-gradient(circle, #ffffff 30%, #f8f9fa 100%)" }}
                ></div>

                <svg className="absolute top-0 left-0" height="100%" viewBox="0 0 390 390" width="100%">
                    {/* Definitions for filters and gradients */}
                    <defs>
                        <filter height="140%" id="shadow" width="140%" x="-20%" y="-20%">
                            <feDropShadow dx="0" dy="1" floodColor="rgba(0,0,0,0.2)" stdDeviation="2" />
                        </filter>
                        <linearGradient id="sleepGradient" x1="0%" x2="100%" y1="0%" y2="100%">
                            <stop offset="0%" stopColor="#3478BB" />
                            <stop offset="100%" stopColor="#2667A7" />
                        </linearGradient>
                        <linearGradient id="wakeGradient" x1="0%" x2="100%" y1="0%" y2="100%">
                            <stop offset="0%" stopColor="#EFD8B8" />
                            <stop offset="100%" stopColor="#E5C9A0" />
                        </linearGradient>
                        <filter height="200%" id="markerGlow" width="200%" x="-50%" y="-50%">
                            <feGaussianBlur result="blur" stdDeviation="1" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                    </defs>

                    {/* Background ring with subtle shadow */}
                    <circle cx="195" cy="195" fill="#f9f9f9" filter="url(#shadow)" r="156" />
                    <circle cx="195" cy="195" fill="white" filter="url(#shadow)" r="91" />

                    {/* Time segments */}
                    {timeRanges.map((range, index) => {
                        const startMinutes = getMinutesSinceWakeTime(range.start);
                        const endMinutes =
                            index === timeRanges.length - 1 ? MINUTES_IN_DAY : getMinutesSinceWakeTime(range.end);

                        const startAngle = (startMinutes / MINUTES_IN_DAY) * 360;
                        const endAngle = (endMinutes / MINUTES_IN_DAY) * 360;
                        const isActive = activeRange === index;
                        const sleepColors = ["#8BBFF3", "#5495D5", "#3478BB", "#2667A7", "#192773"];

                        // Use outer radius of 156 and inner radius of 91 to create a ring
                        const arcPath = calculateArcPath(startAngle, endAngle, 156, 91, 195, 195);

                        // Calculate middle angle for icon placement
                        const middleAngle = (startAngle + endAngle) / 2;
                        const iconRadius = 124; // Position in the middle of the ring
                        const iconPos = calculateLabelPosition(middleAngle, iconRadius, 195, 195);

                        return (
                            <g
                                key={index}
                                onMouseEnter={() => setActiveRange(index)}
                                onMouseLeave={() => setActiveRange(null)}
                            >
                                <path
                                    className="cursor-pointer transition-all duration-200"
                                    d={arcPath}
                                    fill={range.type === "sleep" ? "url(#sleepGradient)" : "url(#wakeGradient)"}
                                    stroke={isActive ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)"}
                                    strokeWidth="1"
                                    style={{
                                        filter: isActive ? "drop-shadow(0px 1px 3px rgba(0,0,0,0.2))" : "none",
                                        transform: isActive ? "scale(1.03)" : "scale(1)",
                                        transformOrigin: "center",
                                    }}
                                />

                                {/* Icon or indicator for the segment */}
                                {/* Removed small white circles as requested */}
                            </g>
                        );
                    })}

                    {/* Time markers */}
                    {timeMarkers
                        .filter((_, index, arr) => {
                            // Filter to show fewer markers for cleaner look
                            if (arr.length <= 6) return true;
                            return index % Math.ceil(arr.length / 6) === 0;
                        })
                        .map((minutes, index) => {
                            const angle = (minutes / MINUTES_IN_DAY) * 360;
                            const labelPos = calculateLabelPosition(angle, 156, 195, 195);
                            const time = moment(wakeTime).add(minutes, "minutes");

                            return (
                                <g key={index}>
                                    {/* Time label */}
                                    <text
                                        dominantBaseline="middle"
                                        fill="#666"
                                        fontSize="14"
                                        fontWeight="500"
                                        textAnchor="middle"
                                        x={labelPos.x}
                                        y={labelPos.y}
                                    >
                                        {time.format("HH:mm")}
                                    </text>
                                </g>
                            );
                        })}

                    {/* Add subtle dividing lines between segments */}
                    {timeRanges.map((range, index) => {
                        const startMinutes = getMinutesSinceWakeTime(range.start);
                        const startAngle = (startMinutes / MINUTES_IN_DAY) * 360;
                        const rad = ((startAngle - 90) * Math.PI) / 180;
                        const x1 = 195 + 91 * Math.cos(rad);
                        const y1 = 195 + 91 * Math.sin(rad);
                        const x2 = 195 + 156 * Math.cos(rad);
                        const y2 = 195 + 156 * Math.sin(rad);

                        return (
                            <line
                                key={`divider-${index}`}
                                stroke="rgba(255,255,255,0.7)"
                                strokeDasharray="2,2"
                                strokeWidth="1"
                                x1={x1}
                                x2={x2}
                                y1={y1}
                                y2={y2}
                            />
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};
